import React from 'react';
import formatDuration from '../../utils/formatDuration';
import type { SearchHit, TodoTask } from '../../types/todo';

// 优先级字块的配色。p1/p2 实心底白字，p3/p4 浅底彩字——四档一视同仁的话，
// 「最低优先级」也在抢注意力（设计稿 docs/design/todo-priority-mockup.html
// 的方案 B）。
//
// 与 tracker/Plan/TaskRow.tsx 的 PRIORITY_META 刻意不共用：那边是 antd Tag
// 的 color 名，这边是 Tailwind 类名，两页视觉语言不同。
const PRIORITY_CLASS: Record<'p1' | 'p2' | 'p3' | 'p4', string> = {
  p1: 'bg-todo-p1 text-white',
  p2: 'bg-todo-p2 text-white',
  p3: 'bg-todo-p3Soft text-todo-p3',
  p4: 'bg-todo-p4Soft text-todo-p4',
};

// 「今天」视图里每条都是今天，写出来是噪音；其他视图里不写，就看不出这条
// 排在哪天——详情栏能设日期，列表却不显示，等于设了看不见。
function plannedLabel(planned: string | null, today: string): string | null {
  if (!planned || planned === today) return null;

  const date = new Date(`${planned}T00:00:00`);
  const todayDate = new Date(`${today}T00:00:00`);
  const days = Math.round((date.getTime() - todayDate.getTime()) / 86_400_000);

  if (days === 1) return '明天';
  if (days === -1) return '昨天';
  return `${date.getMonth() + 1}月${date.getDate()}日`;
}

// 截止日在中栏的说法。与 plannedLabel 的区别：那个在「就是今天」时回 null
// （今天的事出现在「今天」列表里，再写一遍是废话），这个在今天要说「今天截止」
// —— 截止日不进任何视图口径，不写出来就没有任何地方会提到它。
//
// 三种形态一律不着色。留痕但不催：过期只是把陈述换个说法，不是催。
function dueLabel(dueOn: string | null, today: string): string | null {
  if (!dueOn) return null;

  const date = new Date(`${dueOn}T00:00:00`);
  const days = Math.round((date.getTime() - new Date(`${today}T00:00:00`).getTime()) / 86_400_000);

  if (days < 0) return `逾期 ${-days} 天`;
  if (days === 0) return '今天截止';
  return `截止 ${date.getMonth() + 1}月${date.getDate()}日`;
}

// 把命中的那一段套上 <mark>。只标第一处 —— 一行里同一个词标三遍，
// 高亮就不再是「看这里」而是背景色。
function highlight(text: string, q: string): React.ReactNode {
  const at = text.toLowerCase().indexOf(q.toLowerCase());
  if (at < 0 || !q) return text;

  return (
    <>
      {text.slice(0, at)}
      <mark className="rounded-[3px] bg-todo-accent2 px-px text-todo-accent">
        {text.slice(at, at + q.length)}
      </mark>
      {text.slice(at + q.length)}
    </>
  );
}

type TaskRowProps = {
  task: TodoTask;
  today: string;
  selected: boolean;
  running: boolean;
  // 只有未完成的行可拖。已完成的排在折叠组里，顺序没有意义。
  draggable: boolean;
  dragging: boolean;
  dropBefore: boolean;
  onSelect: (task: TodoTask) => void;
  // 右键菜单由页面级组件渲染（中栏是滚动容器，菜单渲染在行内会被裁掉），
  // 这一行只负责把位置报上去。
  onContextMenu: (task: TodoTask, x: number, y: number) => void;
  onToggle: (task: TodoTask) => void;
  onStar: (task: TodoTask) => void;
  onDragStart: (taskId: number) => void;
  onDragOverRow: (taskId: number) => void;
  onDragEnd: () => void;
  onDrop: () => void;
  // 只有搜索结果传。传了就多渲染两样：子任务的「父 › 」前缀，以及命中在
  // 备注 / 留言时那一行灰片段。q 一起传是为了在片段和标题里标出命中处。
  search?: { q: string; hit: SearchHit };
};

const TaskRow: React.FC<TaskRowProps> = ({
  task,
  today,
  selected,
  running,
  draggable,
  dragging,
  dropBefore,
  onSelect,
  onContextMenu,
  onToggle,
  onStar,
  onDragStart,
  onDragOverRow,
  onDragEnd,
  onDrop,
  search,
}) => {
  const doneSteps = task.subtasks.filter((sub) => sub.completed).length;
  const spentMinutes = Math.round(task.spent_seconds / 60);

  const meta: React.ReactNode[] = [];
  const planned = task.completed ? null : plannedLabel(task.planned_for, today);
  if (planned) meta.push(<span key="planned">{planned}</span>);
  // 已完成的不显示截止日：那条已经做完了，「逾期 2 天」只是事后责备。
  // 与上面 planned 的处理同一个理由。
  const due = task.completed ? null : dueLabel(task.due_on, today);
  if (due) meta.push(<span key="due">{due}</span>);
  if (task.project) meta.push(<span key="project">{task.project.name}</span>);
  if (task.subtasks.length > 0) {
    meta.push(
      <span key="steps">
        步骤 {doneSteps}/{task.subtasks.length}
      </span>
    );
  }
  if (task.comments_count > 0) {
    // 用中文短词而不是 💬：这个 meta 数组现在是「明天 · 项目名 · 紧急 ·
    // 步骤 2/5 · 估 30 分钟」清一色的文字，emoji 会是那行里唯一的图形元素。
    meta.push(<span key="comments">留言 {task.comments_count}</span>);
  }
  if (task.linked_worklogs.length > 0) {
    // 用中文短词而不是图标，与上面「留言 n」同一个理由：这一行是清一色的
    // 文字，图形元素会成为唯一的异类。
    //
    // 0 不显示，与「留言 n」「步骤 n/m」同一条规矩 —— meta 行说的是「这条任务
    // 有什么特别的」，每行都挂一个「日志 0」只会摊薄这行的信噪比。
    meta.push(<span key="worklogs">日志 {task.linked_worklogs.length}</span>);
  }
  if (task.linked_wikis.length > 0) {
    // 同上，0 不显示，与「日志 n」并列——两个事实各自独立，任务可以只关联了
    // wiki 没关联日志（比如手动挂了一份长期参考文档）。
    meta.push(<span key="wikis">wiki {task.linked_wikis.length}</span>);
  }
  if (task.agent_comments_count > 0) {
    // 与「留言 n」并列而不是替换它：「有几条留言」和「其中 AI 写过几条」是
    // 两个事实，合成一个数字就分不清了。
    //
    // 这一个用符号而不是中文短词，与上面那条的理由刚好相反：这一行里只有它
    // 需要被一眼扫到 —— 「AI 已经回过话了」是你不点开就该知道的事。
    meta.push(
      <span key="agent-comments" className="text-todo-star">
        ✦ {task.agent_comments_count}
      </span>
    );
  }
  if (task.completed) {
    if (spentMinutes > 0) meta.push(<span key="spent">用了 {formatDuration(spentMinutes)}</span>);
  } else if (task.estimated_minutes) {
    meta.push(
      <span key="estimate">
        估 {formatDuration(task.estimated_minutes)}
        {spentMinutes > 0 && ` · 已 ${formatDuration(spentMinutes)}`}
      </span>
    );
  }

  return (
    // 整行不带 role="button" / tabIndex：里面已经有勾选框和星标两个可交互元素，
    // 套进一个 button 语义里属于嵌套可交互控件，Tab 顺序和读屏语义都是坏的。
    //
    // 行上的 onClick 只是鼠标便利（点哪儿都能开详情，目标更大），键盘和读屏
    // 走的是标题那个真 button。两者并存不冲突。
    <div
      draggable={draggable}
      onClick={() => onSelect(task)}
      onContextMenu={(event) => {
        event.preventDefault();
        onContextMenu(task, event.clientX, event.clientY);
      }}
      onDragStart={(event) => {
        // 不设 dataTransfer 的话 Firefox 不认这次拖拽
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(task.id));
        onDragStart(task.id);
      }}
      onDragOver={(event) => {
        if (!draggable) return;
        event.preventDefault();
        onDragOverRow(task.id);
      }}
      onDrop={(event) => {
        event.preventDefault();
        onDrop();
      }}
      onDragEnd={onDragEnd}
      className={[
        // 窄屏行更高：一是拇指好点，二是命中区的几何依赖 12px 的上内边距
        // （见 application.css 的 .todo-check::before）
        'mb-[5px] flex cursor-pointer items-start gap-[11px] rounded-lg border border-l-[3px] px-3 py-3 shadow-sm sm:py-[10px]',
        dragging ? 'opacity-40' : '',
        // 落点提示画在行的上边，比整行高亮更能说明「会插到这里」
        dropBefore ? 'border-t-2 border-t-todo-accent' : '',
        task.completed ? 'bg-todo-card2 shadow-none' : 'bg-todo-card',
        selected ? 'border-todo-accent' : 'border-todo-line hover:border-todo-line2',
        running ? 'border-l-todo-accent' : 'border-l-transparent',
      ].join(' ')}
    >
      <input
        type="checkbox"
        className="todo-check mt-px"
        checked={task.completed}
        onClick={(event) => event.stopPropagation()}
        onChange={() => onToggle(task)}
        aria-label={task.completed ? '标为未完成' : '标为完成'}
      />

      <button
        type="button"
        onClick={() => onSelect(task)}
        className="min-w-0 flex-1 cursor-pointer text-left"
      >
        <div className={`break-words text-[14px] ${task.completed ? 'text-todo-muted line-through' : ''}`}>
          {/* 优先级字块。放在标题文字之前而不是 meta 行：meta 行在标题下方、
              11.5px 灰色，长 backlog 里根本扫不出来（设计稿的方案 A 就是因此
              被否的）。左侧 3px 竖条也不能用——那是「计时中」的指示器。 */}
          {task.priority && (
            <span
              className={`mr-[7px] inline-block min-w-[21px] rounded px-1 text-center align-[1px] font-mono text-[10.5px] font-bold leading-4 ${PRIORITY_CLASS[task.priority]}`}
            >
              {task.priority.toUpperCase()}
            </span>
          )}
          {/* 子任务行写成「父 › 子」：不写出父任务，它就只剩一个孤零零的短句，
              看不出属于哪件事。

              子任务平权进视图之后这不再是搜索独有的需求 —— 任何视图里都可能
              出现一行子任务。两个来源都读：task.parent 来自 TaskPresenter，
              search.hit.parent_title 是搜索那条路先有的 meta，同源不冲突。 */}
          {(task.parent?.title || search?.hit.parent_title) && (
            <span className="text-todo-muted">
              {task.parent?.title || search?.hit.parent_title} ›{' '}
            </span>
          )}
          {search ? highlight(task.title, search.q) : task.title}
        </div>

        {/* 为什么命中，就写在这一行里 —— 不按命中位置分组（中栏已经有自己的
            分组语言：已完成折叠组，一份列表容不下两套）。 */}
        {search?.hit.snippet && (
          <div className="mt-1 truncate border-l-2 border-todo-line pl-[9px] text-[11.5px] leading-relaxed text-todo-ink2">
            {search.hit.why === 'comment' && (
              <span className="mr-1.5 text-todo-muted">
                {search.hit.comment_author_kind === 'agent' ? 'AI' : '我'} 写的留言
              </span>
            )}
            {highlight(search.hit.snippet, search.q)}
          </div>
        )}

        {(meta.length > 0 || running) && (
          <div className="mt-[3px] flex flex-wrap items-center gap-[11px] text-[11.5px] text-todo-muted">
            {running && <span className="font-semibold text-todo-accent">● 计时中</span>}
            {meta}
          </div>
        )}
      </button>

      <button
        type="button"
        title="重要"
        aria-label={task.important ? '取消重要' : '标为重要'}
        onClick={(event) => {
          event.stopPropagation();
          onStar(task);
        }}
        className={`relative flex-none px-0.5 text-[14px] leading-[19px] before:absolute before:-top-3 before:left-1/2 before:hidden before:h-11 before:w-11 before:-translate-x-1/2 before:content-[''] max-sm:before:block ${
          task.important ? 'text-todo-star' : 'text-todo-line2 hover:text-todo-star'
        }`}
      >
        {task.important ? '★' : '☆'}
      </button>
    </div>
  );
};

export default TaskRow;
